import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useLocation, useNavigate } from 'react-router-dom';
import useStore from '../store';
import { attendanceApi, leavesApi, leavePoliciesApi } from '../services/api';

const DISMISS_KEY_PREFIX = 'aays_attendance_reminder_dismissed_';

const QUICK_STATUSES = [
  { value: 'working',     label: 'Working',     bg: 'rgb(52 211 153 / .16)', color: '#6EE7B7' },
  { value: 'half_day',    label: 'Half Day',    bg: 'rgb(251 191 36 / .16)', color: '#FCD34D' },
  { value: 'not_working', label: 'Not Working', bg: 'rgb(248 113 113 / .16)', color: '#FCA5A5' },
];

// Shown once per day (per browser session) when the logged-in user lands on
// any authenticated page without having marked today's attendance yet —
// skipped on weekends, on an approved leave day, a policy holiday, or if
// already marked/dismissed for today.
export default function AttendanceReminderModal() {
  const { user } = useStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(null);

  const today = new Date().toISOString().split('T')[0];
  const dismissKey = user?.id ? `${DISMISS_KEY_PREFIX}${user.id}_${today}` : null;

  useEffect(() => {
    if (!user?.id || !dismissKey) return;
    if (sessionStorage.getItem(dismissKey)) return;
    if (location.pathname === '/attendance') return;

    const isWeekend = [0, 6].includes(new Date().getDay());
    if (isWeekend) return;

    let cancelled = false;
    const check = async () => {
      try {
        const [recRes, leaveRes] = await Promise.all([
          attendanceApi.byDate(today),
          leavesApi.list({ status: 'approved', userId: user.id, startDate: today, endDate: today }),
        ]);
        if (cancelled) return;
        if (recRes.data.data) return; // already marked today

        const onLeave = (leaveRes.data.data || []).some(
          (l) => l.start_date <= today && l.end_date >= today
        );
        if (onLeave) return;

        let isHoliday = false;
        if (user.leave_policy_id) {
          try {
            const polRes = await leavePoliciesApi.get(user.leave_policy_id);
            isHoliday = (polRes.data.data?.holidays || []).some((h) => h.date === today);
          } catch {
            // ignore — fall through and still show the reminder
          }
        }
        if (cancelled || isHoliday) return;

        setShow(true);
      } catch {
        // Silently skip — don't let this block the rest of the app.
      }
    };
    check();
    return () => { cancelled = true; };
  }, [user?.id, dismissKey, location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMark = async (status) => {
    setSaving(status);
    try {
      await attendanceApi.upsert({
        date: today,
        status,
        time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      toast.success('Attendance marked!');
      setShow(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to mark attendance');
    } finally {
      setSaving(null);
    }
  };

  const handleDismiss = () => {
    if (dismissKey) sessionStorage.setItem(dismissKey, '1');
    setShow(false);
  };

  if (!show) return null;

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 380, textAlign: 'center' }}>
        <div className="modal-body" style={{ padding: '24px 24px 8px' }}>
          <div style={{ fontSize: '1.75rem', marginBottom: 6 }}>📋</div>
          <h3 style={{ marginBottom: 6, fontSize: '1.0625rem' }}>Mark your attendance</h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.8125rem', marginBottom: 14 }}>
            You haven't marked your attendance for today yet.
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            {QUICK_STATUSES.map((s) => (
              <button
                key={s.value}
                className="btn"
                style={{ flex: 1, background: s.bg, color: s.color, fontSize: '0.8125rem' }}
                disabled={!!saving}
                onClick={() => handleMark(s.value)}
              >
                {saving === s.value ? '…' : s.label}
              </button>
            ))}
          </div>
        </div>
        <div className="modal-footer" style={{ justifyContent: 'center', padding: '12px 24px 20px' }}>
          <button className="btn btn-ghost" onClick={handleDismiss}>Remind me later</button>
          <button className="btn btn-primary" onClick={() => { setShow(false); navigate('/attendance'); }}>Open Attendance Page</button>
        </div>
      </div>
    </div>
  );
}
